import { apiUrl, redirectOnDenied, webAuth } from '../auth/web-auth.js';

/**
 * Cliente HTTP da administração.
 *
 * Duas regras que este arquivo aplica:
 *
 * 1. **Identidade central, nunca segredo no navegador.** Toda chamada leva o
 *    Bearer da sessão Sigfarm (WA01), mantido só em memória pelo SDK; o
 *    servidor exige admin global por `sub`. 401/403 de acesso redirecionam.
 * 2. **Recibo ≠ HTTP.** `submit` devolve o corpo E o status, e as telas decidem
 *    pelo ESTADO do recibo. Um 202 com `waiting_dependencies` não é sucesso
 *    aplicado, e um 409 não é "salvo".
 */

export interface ApiResult<T> {
  readonly status: number;
  readonly body: T;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: string,
    readonly detail: string,
  ) {
    super(`${errorCode}: ${detail}`);
    this.name = 'ApiError';
  }
}

const BASE = '/v1';

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  const response = await webAuth().authFetch(apiUrl(`${BASE}${path}`), {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  await redirectOnDenied(response);

  const text = await response.text();
  const parsed = text === '' ? ({} as T) : (JSON.parse(text) as T);
  return { status: response.status, body: parsed };
}

export const api = {
  command: <T>(document: unknown) => call<T>('POST', '/admin/commands/v2', document),
  operations: <T>(envelope: unknown) => call<T>('POST', '/admin/field-operations/v3', envelope),

  list: <T>(path: string) => call<T>('GET', path),

  saveScenario: <T>(document: unknown) => call<T>('POST', '/admin/scenarios', document),
  updateScenario: <T>(scenarioId: string, document: unknown) =>
    call<T>('PUT', `/admin/scenarios/${encodeURIComponent(scenarioId)}`, document),
  listScenarios: <T>(tenantId: string, farmId?: string) => {
    const params = new URLSearchParams({ tenantId });
    if (farmId !== undefined) params.set('farmId', farmId);
    return call<T>('GET', `/admin/scenarios?${params.toString()}`);
  },
  readScenario: <T>(scenarioId: string, tenantId: string) =>
    call<T>('GET', `/admin/scenarios/${scenarioId}?tenantId=${encodeURIComponent(tenantId)}`),
  createPlan: <T>(scenarioId: string, payload: unknown) =>
    call<T>('POST', `/admin/scenarios/${scenarioId}/generation-plans`, payload),
  runPlan: <T>(planId: string, payload: unknown) =>
    call<T>('POST', `/admin/generation-plans/${planId}/run`, payload),
  readPlan: <T>(planId: string, tenantId: string) =>
    call<T>('GET', `/admin/generation-plans/${planId}?tenantId=${encodeURIComponent(tenantId)}`),
  simulationControl: <T>(document: unknown) => call<T>('POST', '/admin/simulation/control', document),
  simulationRun: <T>(runId: string) =>
    call<T>('GET', `/admin/simulation/runs/${encodeURIComponent(runId)}`),
  processingOverview: <T>(tenantId: string) =>
    call<T>('GET', `/admin/processing?tenantId=${encodeURIComponent(tenantId)}`),
  reprocess: <T>(document: unknown) => call<T>('POST', '/admin/processing/reprocess', document),

  // Acesso web (WA01): somente usuários conhecidos pelo RFID.
  accessUsers: <T>(query: { status?: string; search?: string; cursor?: string }) => {
    const params = new URLSearchParams({ limit: '50' });
    for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== '') params.set(key, value);
    return call<T>('GET', `/admin/access/users?${params.toString()}`);
  },
  accessUser: <T>(id: string) => call<T>('GET', `/admin/access/users/${encodeURIComponent(id)}`),
  preRegister: <T>(email: string, grants: unknown[]) =>
    call<T>('POST', '/admin/access/pre-registrations', { email, grants }),
  setAccessStatus: <T>(id: string, status: string) =>
    call<T>('PATCH', `/admin/access/users/${encodeURIComponent(id)}/status`, { status }),
  setAccessGrants: <T>(id: string, grants: unknown[]) =>
    call<T>('PUT', `/admin/access/users/${encodeURIComponent(id)}/grants`, { grants }),
};

/**
 * Traduz um recibo em um resultado de tela.
 *
 * O ponto: `applied` é o ÚNICO desfecho que a interface pode mostrar como
 * sucesso. Conflito, recusa e pendência têm aparência própria — um toast verde
 * em cima de um 409 é como um cadastro que não existe parece existir.
 */
export type OutcomeKind = 'applied' | 'duplicate' | 'conflict' | 'rejected' | 'pending';

export interface Outcome {
  readonly kind: OutcomeKind;
  readonly message: string;
  readonly errorCode: string | null;
}

export function adminOutcome(result: ApiResult<Record<string, unknown>>): Outcome {
  const body = result.body;
  if (body.status === 'applied') {
    return body.duplicate === true
      ? { kind: 'duplicate', message: 'Pedido repetido: nada foi criado de novo.', errorCode: null }
      : { kind: 'applied', message: 'Aplicado.', errorCode: null };
  }
  if (body.status === 'conflict') {
    return {
      kind: 'conflict',
      message: `Conflito: ${String(body.detail ?? 'estado divergente')}`,
      errorCode: (body.errorCode as string) ?? null,
    };
  }
  return {
    kind: 'rejected',
    message: `Recusado: ${String(body.detail ?? 'regra não satisfeita')}`,
    errorCode: (body.errorCode as string) ?? null,
  };
}

export function operationOutcome(result: ApiResult<Record<string, unknown>>): Outcome[] {
  const results = (result.body.results ?? []) as Record<string, unknown>[];
  return results.map((item) => {
    switch (item.state) {
      case 'applied':
        return item.duplicate === true
          ? { kind: 'duplicate' as const, message: 'Operação já aplicada antes.', errorCode: null }
          : { kind: 'applied' as const, message: 'Aplicada.', errorCode: null };
      case 'waiting_dependencies':
      case 'received':
        return {
          kind: 'pending' as const,
          message: 'Recebida e aguardando; ainda NÃO aplicada.',
          errorCode: (item.errorCode as string) ?? null,
        };
      case 'conflict':
        return {
          kind: 'conflict' as const,
          message: `Conflito: ${String(item.detail ?? item.errorCode)}`,
          errorCode: (item.errorCode as string) ?? null,
        };
      default:
        return {
          kind: 'rejected' as const,
          message: `Recusada: ${String(item.detail ?? item.errorCode)}`,
          errorCode: (item.errorCode as string) ?? null,
        };
    }
  });
}
